import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { IMAGES, CAP_STATEMENT_PDF, LOCATIONS } from "../lib/assets";
import { PROJECTS } from "../lib/projects";
import Reveal from "../components/Reveal";
import MarqueeLink from "../components/MarqueeLink";
import Testimonials from "../components/Testimonials";
import ImageSlider from "../components/ImageSlider";
import CrossfadeSlider from "../components/CrossfadeSlider";

const whyPoints = [
  "Reliable and experienced trades team",
  "Consistent finishing standards",
  "Strong communication with site managers",
  "Coordination of multiple interior trades",
  "Experience working alongside builders and developers",
];

const services = [
  { title: "Flooring", img: IMAGES.flooringDetail, to: "/services/flooring" },
  { title: "Tiling", img: IMAGES.tiling, to: "/services/tiling" },
  { title: "Artisan Plastering", img: IMAGES.plastering, to: "/services/artisan-plastering" },
  { title: "Interior Finishing", img: IMAGES.interiorFinishing, to: "/services/interior-finishing" },
  // Waterproofing sits under the Tiling scope, Floor Prep under Flooring.
  { title: "Waterproofing", img: IMAGES.waterproofing, to: "/services/tiling" },
  { title: "Floor Preparation", img: IMAGES.floorPrep, to: "/services/flooring" },
];

const johnStreet = PROJECTS.find((p) => p.id === "151-john-street");

const featured = PROJECTS.filter((p) => p !== johnStreet).map((p) => ({
  tag: p.category,
  title: p.name,
  location: p.location,
  img: p.image,
}));

export default function Home() {
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/p0znykrf_0C6A8316-16.webp",
    "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/hm288aoh_L22SD_IMAGEWORKS-15.webp.jpeg",
    "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/ckq6teh7_Trust%20new%201.jpg",
  ];
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPrev, setHeroPrev] = useState(null);
  const goHero = (next) => {
    if (next === heroIndex) return;
    setHeroPrev(heroIndex);
    setHeroIndex(next);
  };
  useEffect(() => {
    const id = setTimeout(() => goHero((heroIndex + 1) % heroImages.length), 6000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroIndex, heroImages.length]);

  return (
    <div data-testid="home-page">
      {/* HERO — 3-image crossfade slider */}
      <section
        className="relative h-[100vh] min-h-[640px] w-full overflow-hidden bg-[#EDEBE6]"
        data-testid="hero-section"
      >
        {heroImages.map((src, i) => {
          const active = i === heroIndex;
          const under = i === heroPrev;
          return (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden={!active}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                opacity: active || under ? 1 : 0,
                zIndex: active ? 2 : under ? 1 : 0,
                transition: active ? "opacity 2000ms cubic-bezier(0.4, 0, 0.2, 1)" : "none",
              }}
            />
          );
        })}
        <div className="absolute inset-0 z-[3] bg-gradient-to-b from-black/40 via-black/30 to-black/70" />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 h-full flex flex-col justify-end pb-28 md:pb-32">
          <h1 className="font-heading font-medium text-white text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight max-w-3xl">
            Interior finishing for those<br />
            who build with precision.
          </h1>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
          <span className="text-white/70 font-heading uppercase tracking-[0.3em] text-[10px]">
            Scroll to explore
          </span>
          <span className="block w-px h-10 bg-white/40 scroll-cue" />
        </div>

        <div className="absolute bottom-8 right-8 z-10 hidden md:flex gap-3">
          {heroImages.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              onClick={() => goHero(i)}
              className={`h-px transition-all duration-500 ${
                i === heroIndex ? "w-12 bg-white" : "w-6 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </section>

      {/* SPECIALISTS */}
      <section className="py-32 md:py-48" data-testid="specialists-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-end">
          <div className="md:col-span-5">
            <Reveal>
              <span className="eyebrow">Specialists</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] leading-[1.3] font-medium tracking-tight">
                One team. Every interior trade. Site-ready from lining to final finish.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={120}>
              <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden">
                <img
                  src={IMAGES.minimalLiving}
                  alt="Architectural interior"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW — image background */}
      <section
        className="relative overflow-hidden"
        data-testid="about-preview"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/n7qpthpq_microcement-contemporary-style.webp"
          alt="Microcement architectural interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-48">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">About</span>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-10 font-heading text-3xl md:text-4xl lg:text-5xl text-white leading-[1.15] font-medium tracking-tight max-w-[20ch]">
              We bring interiors to life through precision and coordination.
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <p className="text-white/75 text-base md:text-lg max-w-md leading-relaxed">
                Trusted by builders, developers and project managers for clean
                workmanship and dependable delivery.
              </p>
              <MarqueeLink to="/about" variant="ghost" data-testid="about-preview-link">
                Who we are
              </MarqueeLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED — 151 JOHN STREET */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]" data-testid="john-street-feature">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-end">
          <div className="md:col-span-7">
            <Reveal>
              <Link to={johnStreet.page} className="group block overflow-hidden aspect-[16/10] bg-[#EDEBE6]" data-testid="john-street-feature-image">
                <CrossfadeSlider
                  images={johnStreet.featureSlides}
                  alt="151 John Street — engineered oak flooring"
                  testid="john-street-feature-slider"
                />
              </Link>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={120}>
              <span className="eyebrow">Featured Project</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] leading-[1.2] font-medium tracking-tight">
                151 John Street
              </h2>
              <p className="mt-3 text-[#545454] text-sm" data-testid="john-street-feature-meta">Residential Flooring | Auckland</p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-8 font-heading text-[#111111] text-base md:text-lg leading-snug max-w-md">
                A showcase of natural materials, considered detailing and quality workmanship.
              </p>
              <p className="mt-6 text-[#545454] text-sm md:text-base leading-relaxed max-w-md">
                {johnStreet.blurb}
              </p>
              <p className="mt-4 text-[#545454] text-sm md:text-base leading-relaxed max-w-md">
                From preparation through to final installation, every detail was carefully considered to achieve a timeless result.
              </p>
            </Reveal>
            <Reveal delay={280}>
              <div className="mt-12">
                <MarqueeLink to={johnStreet.page} variant="ghost-dark" data-testid="john-street-feature-link">
                  Explore this project
                </MarqueeLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICE COLLECTION */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]" data-testid="services-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20 md:mb-28">
            <Reveal>
              <span className="eyebrow">Service Collection</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] leading-[1.2] font-medium tracking-tight max-w-xl">
                Built for considered interior architecture.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <MarqueeLink to="/services" variant="ghost-dark" data-testid="services-all-link">
                Service overview
              </MarqueeLink>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16">
            {services.slice(0, 3).map((s, i) => (
              <Reveal key={i} delay={i * 60}>
                <Link
                  to={s.to}
                  data-testid={`service-tile-${i}`}
                  className="group block"
                >
                  <div className="overflow-hidden aspect-[3/4] bg-[#111]">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover brightness-[0.78] saturate-[0.9] group-hover:brightness-[1.05] group-hover:saturate-100 group-hover:scale-[1.04] transition-all duration-[1200ms] ease-out"
                    />
                  </div>
                  <p className="mt-5 font-heading text-sm md:text-base text-[#111111] tracking-tight group-hover:text-[#EB1D25] transition-colors">
                    {s.title}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16 mt-16">
            {services.slice(3).map((s, i) => (
              <Reveal key={i + 3} delay={i * 60}>
                <Link
                  to={s.to}
                  data-testid={`service-tile-${i + 3}`}
                  className="group block"
                >
                  <div className="overflow-hidden aspect-[3/4] bg-[#111]">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover brightness-[0.78] saturate-[0.9] group-hover:brightness-[1.05] group-hover:saturate-100 group-hover:scale-[1.04] transition-all duration-[1200ms] ease-out"
                    />
                  </div>
                  <p className="mt-5 font-heading text-sm md:text-base text-[#111111] tracking-tight group-hover:text-[#EB1D25] transition-colors">
                    {s.title}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BUILDERS WORK WITH US — minimal stacked list */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]" data-testid="why-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow">Why builders work with us</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] leading-[1.2] font-medium tracking-tight">
                A single trade partner, dependable on site.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <ul>
              {whyPoints.map((p, i) => (
                <Reveal key={i} delay={i * 80} as="li">
                  <div className="flex items-baseline gap-8 py-7 border-b border-[#E2E2DF]">
                    <span className="font-heading text-xs tracking-[0.25em] text-[#EB1D25] font-semibold w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#111111] text-base md:text-lg font-heading font-normal">
                      {p}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="relative overflow-hidden" data-testid="locations-section">
        <img
          src={IMAGES.commercialHall}
          alt="Architectural commercial interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-48">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">Where We Work</span>
            <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-white leading-[1.2] font-medium tracking-tight max-w-xl">
              A place where precision and execution connect.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-14">
              <div className="border-t border-white/30 pt-6">
                <p className="text-white/60 text-[11px] uppercase tracking-[0.25em] font-heading">Auckland</p>
                <p className="mt-3 text-white text-base md:text-lg font-heading">28 Constellation Drive</p>
              </div>
              <div className="border-t border-white/30 pt-6">
                <p className="text-white/60 text-[11px] uppercase tracking-[0.25em] font-heading">Christchurch</p>
                <p className="mt-3 text-white text-base md:text-lg font-heading">South Island — TBC</p>
              </div>
              <div className="border-t border-white/30 pt-6">
                <p className="text-white/60 text-[11px] uppercase tracking-[0.25em] font-heading">Queenstown</p>
                <p className="mt-3 text-white text-base md:text-lg font-heading">South Island — TBC</p>
              </div>
              <div className="border-t border-white/30 pt-6 md:flex md:items-end md:justify-end">
                <MarqueeLink to="/contact" variant="ghost" data-testid="locations-cta">
                  Visit us
                </MarqueeLink>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]" data-testid="featured-projects-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20 md:mb-28">
            <Reveal>
              <span className="eyebrow">Featured Projects</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] leading-[1.2] font-medium tracking-tight">
                Each project tells its own story.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <MarqueeLink to="/projects" variant="ghost-dark" data-testid="featured-all-link">
                View projects
              </MarqueeLink>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-16">
            {featured.map((p, i) => (
              <Reveal key={i} delay={i * 100}>
                <Link
                  to="/projects"
                  data-testid={`featured-project-${i}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden aspect-[3/4] bg-[#111]">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1500ms] ease-out"
                    />
                  </div>
                  <div className="pt-5 flex items-baseline justify-between gap-4">
                    <p className="font-heading text-sm md:text-base text-[#111111] tracking-tight group-hover:text-[#EB1D25] transition-colors">
                      {p.title}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#545454]">
                      {p.tag}
                    </p>
                  </div>
                  <p className="mt-1 text-[#545454] text-sm">{p.location}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MOOD SLIDER */}
      <ImageSlider
        testid="mood-slider"
        images={[
          "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/tpoceh9l_Trust%20new%201.jpg",
          "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/tziux7ry_Trust%20Flooring%202.jpg",
          "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/jyiv5ad4_ce5c18de-e2a7-496e-acd0-590cb879ded3.JPG",
          "https://customer-assets.emergentagent.com/job_trust-nz-build/artifacts/3td03i1j_IMG_20210326_093513.jpeg",
        ]}
      />

      {/* CLIENT STORIES */}
      <Testimonials />

      {/* CAP STATEMENT — minimal */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]" data-testid="cap-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-baseline">
            <div className="md:col-span-2">
              <span className="eyebrow-muted">CAP Statement</span>
            </div>
            <div className="md:col-span-7">
              <Reveal>
                <p className="font-heading text-xl md:text-2xl text-[#111111] leading-snug font-medium tracking-tight">
                  Download our Company Assessment of Performance statement.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-3 md:text-right">
              <Reveal delay={120}>
                <a
                  href={CAP_STATEMENT_PDF}
                  download="Trust-Flooring-CAP-Statement.pdf"
                  data-testid="cap-download-button"
                  className="inline-flex items-center gap-2 text-[#EB1D25] font-heading uppercase tracking-[0.22em] text-[11px] font-semibold border-b border-[#EB1D25] pb-1 hover:text-[#111111] hover:border-[#111111] transition-colors"
                >
                  Download <Download size={12} strokeWidth={1.5} />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="relative overflow-hidden border-t border-[#E2E2DF]"
        data-testid="home-cta-banner"
      >
        <img
          src={IMAGES.hero}
          alt="Architectural interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/80" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-40 md:py-56">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">Let's Begin</span>
            <h2 className="mt-8 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.1] max-w-3xl">
              Where precision meets the build.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-10 text-white/70 text-base md:text-lg max-w-xl leading-relaxed">
              Every great build begins with understanding.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-14 flex flex-col sm:flex-row gap-4">
              <MarqueeLink to="/contact" variant="primary" data-testid="home-cta-contact">
                Get in touch
              </MarqueeLink>
              <MarqueeLink to="/about" variant="ghost" data-testid="home-cta-about">
                Our approach
              </MarqueeLink>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
